import type { ISODate, Period } from "./types";
import { addDays, toDay } from "./dates";

export type CalendarIssue =
  | { kind: "overlap"; period: number }
  | { kind: "gap"; period: number }
  | { kind: "startAfterCutoff"; period: number }
  | { kind: "badHours"; period: number }
  | { kind: "numbering"; period: number };

/** Sort and sanity-check a calendar. Periods must be contiguous: each start is the day after the previous cutoff. */
export function checkCalendar(periods: Period[]): CalendarIssue[] {
  const issues: CalendarIssue[] = [];
  const ps = [...periods].sort((a, b) => a.number - b.number);
  ps.forEach((p, i) => {
    if (p.start > p.cutoff) issues.push({ kind: "startAfterCutoff", period: p.number });
    if (!(p.stdHours > 0)) issues.push({ kind: "badHours", period: p.number });
    if (i > 0) {
      const prev = ps[i - 1];
      if (p.number === prev.number) issues.push({ kind: "numbering", period: p.number });
      const expected = addDays(prev.cutoff, 1);
      if (p.start < expected) issues.push({ kind: "overlap", period: p.number });
      else if (p.start > expected) issues.push({ kind: "gap", period: p.number });
    }
  });
  return issues;
}

/**
 * Rule 8 / R15: a date is booked in the period whose [start, cutoff] contains it.
 * Rule 18: dates before the calendar start or after its end are errors (returns -1 / Infinity markers via result kind).
 */
export function periodIndexOf(periods: Period[], d: ISODate): number | "before" | "after" {
  if (!periods.length) return "after";
  if (d < periods[0].start) return "before";
  if (d > periods[periods.length - 1].cutoff) return "after";
  let lo = 0;
  let hi = periods.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (periods[mid].cutoff >= d) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

export interface GenerateOptions {
  start: ISODate;
  end: ISODate;
  lengthDays: number;
  stdHours: number;
  firstNumber?: number;
}

/**
 * V17 calendar generator: fixed-length periods from start until the period containing `end`.
 * Month End is suggested (pre-ticked) on the last cutoff of each calendar month; the user can change it (rule 18, design flag 12).
 */
export function generateCalendar(o: GenerateOptions): Period[] {
  const out: Period[] = [];
  let s = o.start;
  let n = o.firstNumber ?? 1;
  if (o.lengthDays < 1) throw new Error("Period length must be at least 1 day");
  while (s <= o.end) {
    const cutoff = addDays(s, o.lengthDays - 1);
    out.push({ number: n++, start: s, cutoff, stdHours: o.stdHours, monthEnd: false });
    s = addDays(cutoff, 1);
    if (out.length > 5000) throw new Error("Calendar too long");
  }
  return suggestMonthEnds(out);
}

/** Tick the last cutoff falling in each calendar month. */
export function suggestMonthEnds(periods: Period[]): Period[] {
  return periods.map((p, i) => {
    const next = periods[i + 1];
    const monthEnd = !next || next.cutoff.slice(0, 7) !== p.cutoff.slice(0, 7);
    return { ...p, monthEnd };
  });
}

export function daysBetween(a: ISODate, b: ISODate): number {
  return toDay(b) - toDay(a);
}
